import { DeleteOutlined, EditOutlined, PlusOutlined, SyncOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Form, Image, Input, InputNumber, message, Modal, Select, Space, Table, TableColumnsType, Tag, Tour, ConfigProvider } from 'antd';
import type { TourProps } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useDrivePicker from 'react-google-drive-picker';
import { bottledDrinkService } from '../services/bottledDrinkService';
import { flavorService } from '../services/flavorService';
import { ingredientService } from '../services/ingredientService';
import { regionService } from '../services/regionService';
import { DishService } from '../services/dishService';
import type { BottledDrink } from '../types/bottledDrink';
import type { Flavor } from '../types/flavor';
import { Ingredient } from '../types/ingredient';
import type { Region } from '../types/region';
import type { Dish } from '../types/dish';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import AdminLayout from '../components/AdminLayout';
import '../styles/AdminPages.css';
import viVN from 'antd/locale/vi_VN';
import BottledDrinkManagementPreview from '../preview/BottledDrinkManagement.jpg';

const { TextArea, Search } = Input;

const SortableImageItem = ({
  id,
  children,
  onRemove
}: {
  id: string;
  children: React.ReactNode;
  onRemove: () => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    cursor: 'move',
  };

  return (
    <Tag
      ref={setNodeRef}
      style={style}
      color="blue"
      closable
      onClose={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onRemove();
      }}
      {...attributes}
      {...listeners}
    >
      {children}
    </Tag>
  );
};

const BottledDrinkForm: React.FC<{
  onImagesChange: (files: File[]) => void;
  onDriveImageSelect: (fileId: string, webViewLink: string, fileName: string) => void;
  onImagesPreviewChange?: (files: any[]) => void;
  onDriveImagePreviewSelect?: (fileId: string, webViewLink: string, fileName: string) => void;
  selectedFiles: File[];
  selectedFilesPreview?: any[];
  driveImages: { fileId: string; webViewLink: string; fileName: string; }[];
  driveImagesPreview?: { fileId: string; webViewLink: string; fileName: string; }[];
  editingDrink?: BottledDrink | null;
  isMobile?: boolean; // Add new prop
  onRemoveLocalImage: (index: number) => void;
  onRemoveLocalPreviewImage: (index: number) => void;
  onRemoveDriveImage: (index: number) => void;
  onRemoveDrivePreviewImage: (index: number) => void;
  setDriveImages: React.Dispatch<React.SetStateAction<{ fileId: string; webViewLink: string; fileName: string; }[]>>;
  setDriveImagesPreview: React.Dispatch<React.SetStateAction<{ fileId: string; webViewLink: string; fileName: string; }[]>>;
  regions: Region[];
  flavors: Flavor[];
  ingredients: Ingredient[];
  regionsLoading: boolean;
  flavorsLoading: boolean;
  ingredientsLoading: boolean;
  // Add refs for modal tour
  nameFieldRef?: React.RefObject<any>;
  descriptionFieldRef?: React.RefObject<any>;
  productCodeFieldRef?: React.RefObject<any>;
  coffeeFieldRef?: React.RefObject<any>;
  originFieldRef?: React.RefObject<any>;
  flavorFieldRef?: React.RefObject<any>;
  ingredientsFieldRef?: React.RefObject<any>;
  volumesFieldRef?: React.RefObject<any>;
  expirationFieldRef?: React.RefObject<any>;
  imagesFieldRef?: React.RefObject<any>;
}> = ({
  onImagesChange,
  onDriveImageSelect,
  onImagesPreviewChange,
  onDriveImagePreviewSelect,
  selectedFiles,
  selectedFilesPreview,
  driveImages,
  driveImagesPreview,
  editingDrink,
  isMobile = false,
  onRemoveLocalImage,
  onRemoveLocalPreviewImage,
  onRemoveDriveImage,
  onRemoveDrivePreviewImage,
  setDriveImages,
  setDriveImagesPreview,
  regions,
  flavors,
  ingredients,
  regionsLoading,
  flavorsLoading,
  ingredientsLoading,
  // Destructure refs
  nameFieldRef,
  descriptionFieldRef,
  productCodeFieldRef,
  coffeeFieldRef,
  originFieldRef,
  flavorFieldRef,
  ingredientsFieldRef,
  volumesFieldRef,
  expirationFieldRef,
  imagesFieldRef,
}) => {
    const [openPicker] = useDrivePicker();

    useEffect(() => {
      if (!editingDrink) {
        onImagesChange([]);
      }
    }, [editingDrink, onImagesChange]);

    const handleRegionChange = async (values: string[]) => {
      // const lastValue = values[values.length - 1];
      // const existingRegion = regions.find(r => r.name === lastValue);

      // if (!existingRegion) {
      //     try {
      //         await regionService.addRegion({
      //             name: lastValue,
      //             description: `Vùng trồng ${lastValue}`,
      //             country: '',
      //             altitude: {
      //                 min: 0,
      //                 max: 0
      //             },
      //             climate: '',
      //             soil: '',
      //             createdAt: new Date(),
      //             updatedAt: new Date()
      //         });
      //         fetchRegions();
      //     } catch (error) {
      //         console.error('Error adding new region:', error);
      //         message.error('Không thể thêm vùng trồng mới');
      //     }
      // }
    };


    const handleFlavorChange = async (values: string[]) => {
      // const lastValue = values[values.length - 1];
      // const existingFlavor = flavors.find(f => f.name === lastValue);

      // if (!existingFlavor) {
      //     try {
      //         await flavorService.addFlavor({
      //             name: lastValue,
      //             iconUrl: '',
      //             description: `Hương vị ${lastValue}`,
      //             intensity: 5,
      //             createdAt: new Date(),
      //             updatedAt: new Date()
      //         });
      //         fetchFlavors();
      //     } catch (error) {
      //         console.error('Error adding new flavor:', error);
      //         message.error('Không thể thêm hương vị mới');
      //     }
      // }
    };


    const handleIngredientChange = async (values: string[]) => {
      // const lastValue = values[values.length - 1];
      // const existingIngredient = ingredients.find(i => i.name === lastValue);

      // if (!existingIngredient) {
      //     try {
      //         await ingredientService.addIngredient(lastValue);
      //         fetchIngredients();
      //     } catch (error) {
      //         console.error('Error adding new ingredient:', error);
      //         message.error('Không thể thêm thành phần mới');
      //     }
      // }
    };

    const handleImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      onImagesChange([...selectedFiles, ...files]);
    };

    const localImagePreviews = useMemo(
      () => selectedFiles.map((file) => URL.createObjectURL(file)),
      [selectedFiles]
    );

    useEffect(() => {
      return () => {
        localImagePreviews.forEach((url) => URL.revokeObjectURL(url));
      };
    }, [localImagePreviews]);

    const handleOpenPicker = () => {
      openPicker({
        clientId: "85231195954-hsetf3cpqpfcr67p2k40001og3054t52.apps.googleusercontent.com",
        developerKey: "AIzaSyD-xnVTuCx7ZDPzxrTmeL4my9FuIfkpYtY",
        viewId: "DOCS",
        showUploadView: true,
        showUploadFolders: true,
        supportDrives: true,
        multiselect: true,
        callbackFunction: (data) => {
          if (data.action === 'picked') {
            data.docs.forEach(file => {
              onDriveImageSelect(file.id, file.url || '', file.name);
            });
          }
        },
      });
    };

    const sensors = useSensors(
      useSensor(PointerSensor),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      })
    );

    const handleDragEnd = (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        if (active.id.toString().startsWith('local_')) {
          const oldIndex = selectedFiles.findIndex((_, i) => `local_${i}` === active.id);
          const newIndex = selectedFiles.findIndex((_, i) => `local_${i}` === over.id);
          onImagesChange(arrayMove(selectedFiles, oldIndex, newIndex));
          message.success('Thay đổi vị trí ảnh thành công');
        }
        else if (active.id.toString().startsWith('preview_') && selectedFilesPreview && onImagesPreviewChange) {
          const oldIndex = selectedFilesPreview.findIndex((_, i) => `preview_${i}` === active.id);
          const newIndex = selectedFilesPreview.findIndex((_, i) => `preview_${i}` === over.id);
          onImagesPreviewChange(arrayMove(selectedFilesPreview, oldIndex, newIndex));
          message.success('Thay đổi vị trí ảnh thành công');
        }
        else if (active.id.toString().startsWith('drive_')) {
          const oldIndex = driveImages.findIndex((_, i) => `drive_${i}` === active.id);
          const newIndex = driveImages.findIndex((_, i) => `drive_${i}` === over.id);
          setDriveImages(arrayMove(driveImages, oldIndex, newIndex));
          message.success('Thay đổi vị trí ảnh thành công');
        }
        else if (active.id.toString().startsWith('drive_preview_') && driveImagesPreview) {
          const oldIndex = driveImagesPreview.findIndex((_, i) => `drive_preview_${i}` === active.id);
          const newIndex = driveImagesPreview.findIndex((_, i) => `drive_preview_${i}` === over.id);
          setDriveImagesPreview(arrayMove(driveImagesPreview, oldIndex, newIndex));
          message.success('Thay đổi vị trí ảnh thành công');
        }
      }
    };

    return (
      <div className={`admin-form-container ${isMobile ? 'mobile-coffee-form' : ''}`}>
        <div className="admin-form-section">
          <h3 className="admin-form-section-title">Thông tin cơ bản</h3>
          <div ref={nameFieldRef}>
            <Form.Item
              name="name"
              label="Tên đồ uống"
              rules={[{ required: true, message: 'Vui lòng nhập tên đồ uống' }]}
            >
              <Input placeholder="Nhập tên đồ uống (VD: Cold Brew Original)" />
            </Form.Item>
          </div>

          <div ref={productCodeFieldRef}>
            <Form.Item
              name="productCode"
              label="Mã đồ uống"
              tooltip="Mã này sẽ được dùng để đồng bộ giá với quản lý món ăn"
            >
              <Input placeholder="Nhập mã đồ uống (VD: CB001)" />
            </Form.Item>
          </div>

          <div ref={descriptionFieldRef}>
            <Form.Item
              name="description"
              label="Mô tả"
              rules={[{ required: true, message: 'Vui lòng nhập mô tả đồ uống' }]}
            >
              <TextArea
                rows={4}
                placeholder="Nhập mô tả về đồ uống (VD: Cà phê pha lạnh với hương vị đậm đà, thơm ngon...)"
              />
            </Form.Item>
          </div>

          <div ref={coffeeFieldRef}>
            <Form.Item
              name="coffeeOriginText"
              label="Loại cà phê"
              rules={[{ required: true, message: 'Vui lòng nhập loại cà phê' }]}
            >
              <Input placeholder="Nhập loại cà phê sử dụng (VD: Arabica Cầu Đất)" />
            </Form.Item>
          </div>
        </div>

        <div className="admin-form-section">
          <h3 className="admin-form-section-title">Thông tin nguồn gốc và hương vị</h3>
          <div ref={originFieldRef}>
            <Form.Item
              name="origin"
              label="Nguồn gốc"
              rules={[{ required: true, message: 'Vui lòng chọn nguồn gốc' }]}
            >
              <Select
                mode="tags"
                loading={regionsLoading}
                onChange={handleRegionChange}
                placeholder="Chọn hoặc nhập nguồn gốc (VD: Cầu Đất, Đà Lạt...)"
              >
                {regions.map(region => (
                  <Select.Option key={region.id} value={region.name}>
                    {region.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <div ref={flavorFieldRef}>
            <Form.Item
              name="flavorNotes"
              label="Hương vị"
              rules={[{ required: true, message: 'Vui lòng chọn hương vị' }]}
            >
              <Select
                mode="tags"
                loading={flavorsLoading}
                onChange={handleFlavorChange}
                placeholder="Chọn hoặc nhập hương vị (VD: Chocolate, Caramel...)"
              >
                {flavors.map(flavor => (
                  <Select.Option key={flavor.id} value={flavor.name}>
                    {flavor.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <div ref={ingredientsFieldRef}>
            <Form.Item
              name="ingredients"
              label="Thành phần"
              rules={[{ required: true, message: 'Vui lòng chọn thành phần' }]}
            >
              <Select
                mode="tags"
                loading={ingredientsLoading}
                onChange={handleIngredientChange}
                placeholder="Chọn hoặc nhập thành phần (VD: Cà phê, Đường...)"
              >
                {ingredients.map(ingredient => (
                  <Select.Option key={ingredient.id} value={ingredient.name}>
                    {ingredient.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        </div>

        <div className="admin-form-section">
          <h3 className="admin-form-section-title">Thông tin sản phẩm</h3>
          <div className="admin-form-row">
            <div ref={expirationFieldRef}>
              <Form.Item
                name="expirationDays"
                label="Số ngày hết hạn"
                rules={[{ required: true, message: 'Vui lòng nhập số ngày hết hạn' }]}
              >
                <InputNumber
                  min={1}
                  placeholder="Nhập số ngày (VD: 30)"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </div>

            <Form.Item
              name="purchaseCount"
              label="Số lượng mua"
              tooltip="Số lượng mua ban đầu, mặc định là 0 nếu không nhập"
            >
              <InputNumber
                min={0}
                placeholder="Nhập số lượng mua"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </div>

          <div ref={volumesFieldRef}>
            <Form.Item label={<><span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span> Dung tích & Giá </>}>
              <Form.List
                name="volumes"
                initialValue={[{}]}
              >
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name }) => (
                      <div key={key} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                        <Form.Item
                          name={[name, 'volume']}
                          rules={[{ required: true, message: 'Vui lòng nhập dung tích' }]}
                          style={{ flex: 1, marginBottom: 0 }}
                        >
                          <InputNumber
                            min={1}
                            placeholder="Nhập dung tích (VD: 250)"
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                        <Form.Item
                          name={[name, 'price']}
                          rules={[{ required: true, message: 'Vui lòng nhập giá' }]}
                          style={{ flex: 1, marginBottom: 0 }}
                        >
                          <InputNumber
                            min={0}
                            placeholder="Nhập giá (VD: 35000)"
                            style={{ width: '100%' }}
                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          />
                        </Form.Item>
                        {fields.length > 1 && (
                          <Button type="link" danger onClick={() => remove(name)}>
                            Xóa
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      style={{ marginTop: 8 }}
                    >
                      + Thêm dung tích và giá
                    </Button>
                  </>
                )}
              </Form.List>
            </Form.Item>
          </div>
        </div>

        <div className="admin-form-section">
          <h3 className="admin-form-section-title">Hình ảnh sản phẩm</h3>
          <Form.Item
            label={<>Hình ảnh</>}
            required
            rules={[
              {
                validator: async () => {
                  if (selectedFiles.length === 0 && driveImages.length === 0 &&
                    (!editingDrink || (!editingDrink.images?.length && !editingDrink.driveImages?.length))) {
                    throw new Error('Vui lòng chọn ít nhất một hình ảnh');
                  }
                }
              }
            ]}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Space>
                <Button onClick={() => document.getElementById('fileInput')?.click()}>
                  Chọn ảnh từ local
                </Button>
                <Button onClick={handleOpenPicker}>
                  Chọn ảnh từ Google Drive
                </Button>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImagesChange}
                  id="fileInput"
                  style={{ display: 'none' }}
                />
              </Space>

              <div className="admin-image-preview">
                {/* Local Files Preview */}
                {selectedFiles.length > 0 && (
                  <div style={{ marginTop: 8, width: '100%' }}>
                    <div>Ảnh từ local:</div>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={selectedFiles.map((_, index) => `local_${index}`)}
                        strategy={verticalListSortingStrategy}
                      >
                        <Space wrap>
                          {selectedFiles.map((file, index) => (
                            <SortableImageItem
                              key={`local_${index}`}
                              id={`local_${index}`}
                              onRemove={() => onRemoveLocalImage(index)}
                            >
                              <Space>
                                {'File local'}
                                <Image
                                  src={localImagePreviews[index]}
                                  alt={file.name}
                                  style={{ width: 50, height: 50, objectFit: 'cover' }}
                                />
                              </Space>
                            </SortableImageItem>
                          ))}
                        </Space>
                      </SortableContext>
                    </DndContext>
                  </div>
                )}

                {/* Drive Images Preview */}
                {driveImages.length > 0 && (
                  <div style={{ marginTop: 8, width: '100%' }}>
                    <div>Ảnh từ Drive:</div>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={driveImages.map((_, index) => `drive_${index}`)}
                        strategy={verticalListSortingStrategy}
                      >
                        <Space wrap>
                          {driveImages.map((image, index) => (
                            <SortableImageItem
                              key={`drive_${index}`}
                              id={`drive_${index}`}
                              onRemove={() => onRemoveDriveImage(index)}
                            >
                              <Space>
                                {'Google Drive'}
                                <Image
                                  src={`https://lh3.googleusercontent.com/d/${image.fileId}?authuser=server`}
                                  alt={image.fileName}
                                  style={{ width: 50, height: 50, objectFit: 'cover' }}
                                />
                              </Space>
                            </SortableImageItem>
                          ))}
                        </Space>
                      </SortableContext>
                    </DndContext>
                  </div>
                )}

                {/* Existing Images Preview */}
                {selectedFilesPreview && selectedFilesPreview.length > 0 && (
                  <div style={{ marginTop: 8, width: '100%' }}>
                    <div>Ảnh hiện tại:</div>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={selectedFilesPreview.map((_, index) => `preview_${index}`)}
                        strategy={verticalListSortingStrategy}
                      >
                        <Space wrap>
                          {selectedFilesPreview.map((file, index) => (
                            <SortableImageItem
                              key={`preview_${index}`}
                              id={`preview_${index}`}
                              onRemove={() => onRemoveLocalPreviewImage(index)}
                            >
                              <Space>
                                {'File local'}
                                <Image
                                  src={file}
                                  alt={`file-${index}`}
                                  style={{ width: 50, height: 50, objectFit: 'cover' }}
                                />
                              </Space>
                            </SortableImageItem>
                          ))}
                        </Space>
                      </SortableContext>
                    </DndContext>
                  </div>
                )}

                {driveImagesPreview && driveImagesPreview.length > 0 && (
                  <div style={{ marginTop: 8, width: '100%' }}>
                    <div>Ảnh Drive hiện tại:</div>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={driveImagesPreview.map((_, index) => `drive_preview_${index}`)}
                        strategy={verticalListSortingStrategy}
                      >
                        <Space wrap>
                          {driveImagesPreview.map((image, index) => (
                            <SortableImageItem
                              key={`drive_preview_${index}`}
                              id={`drive_preview_${index}`}
                              onRemove={() => onRemoveDrivePreviewImage(index)}
                            >
                              <Space>
                                {'Google Drive'}
                                <Image
                                  src={`https://lh3.googleusercontent.com/d/${image.fileId}?authuser=server`}
                                  alt={image.fileName}
                                  style={{ width: 50, height: 50, objectFit: 'cover' }}
                                />
                              </Space>
                            </SortableImageItem>
                          ))}
                        </Space>
                      </SortableContext>
                    </DndContext>
                  </div>
                )}
              </div>
            </div>
          </Form.Item>
        </div>
      </div>
    );
  };

const BottledDrinkManagement: React.FC = () => {
  const [drinks, setDrinks] = useState<BottledDrink[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingDrink, setEditingDrink] = useState<BottledDrink | null>(null);
  const [form] = Form.useForm();
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imageFilesPreview, setImageFilesPreview] = useState<any[]>([]);
  const [driveImages, setDriveImages] = useState<{ fileId: string; webViewLink: string; fileName: string; }[]>([]);
  const [driveImagesPreview, setDriveImagesPreview] = useState<{ fileId: string; webViewLink: string; fileName: string; }[]>([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth <= 400;
  const [regions, setRegions] = useState<Region[]>([]);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [flavors, setFlavors] = useState<Flavor[]>([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [regionFilter, setRegionFilter] = useState<string>('');
  const [flavorFilter, setFlavorFilter] = useState<string>('');
  const [ingredientFilter, setIngredientFilter] = useState<string>('');
  const [syncLoading, setSyncLoading] = useState(false);
  const [lookupsLoading, setLookupsLoading] = useState(false);
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const dishesRef = useRef<Dish[] | null>(null);
  const dishService = useMemo(() => new DishService(), []);
  const isMountedRef = useRef(true);
  const drinksFetchPromiseRef = useRef<Promise<BottledDrink[]> | null>(null);
  const lookupsFetchPromiseRef = useRef<Promise<void> | null>(null);

  // Tour state and refs
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [isModalTourOpen, setIsModalTourOpen] = useState(false);
  const titleRef = useRef(null);
  const searchRef = useRef(null);
  const filtersRef = useRef(null);
  const syncButtonRef = useRef(null);
  const addButtonRef = useRef(null);
  const tableContainerRef = useRef(null);

  // Modal tour refs
  const modalTitleRef = useRef(null);
  const nameFieldRef = useRef(null);
  const descriptionFieldRef = useRef(null);
  const productCodeFieldRef = useRef(null);
  const coffeeFieldRef = useRef(null);
  const originFieldRef = useRef(null);
  const flavorFieldRef = useRef(null);
  const ingredientsFieldRef = useRef(null);
  const volumesFieldRef = useRef(null);
  const expirationFieldRef = useRef(null);
  const imagesFieldRef = useRef(null);
  const submitButtonRef = useRef(null);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchDrinks = useCallback(async (opts?: { force?: boolean }) => {
    const force = Boolean(opts?.force);
    if (!force && drinksFetchPromiseRef.current) return drinksFetchPromiseRef.current;

    setLoading(true);
    const p = bottledDrinkService.getAllBottledDrinks()
      .then((data) => {
        if (!isMountedRef.current) return data;
        setDrinks((prev) => {
          // Tránh setState thừa nếu dữ liệu không đổi (giảm re-render)
          if (prev === data) return prev;
          if (prev.length === data.length && prev.every((item, i) => item.id === data[i]?.id && item.updatedAt === data[i]?.updatedAt)) {
            return prev;
          }
          return data;
        });
        return data;
      })
      .catch((error) => {
        console.error('Error fetching drinks:', error);
        if (isMountedRef.current) message.error('Không thể tải danh sách đồ uống');
        return [];
      })
      .finally(() => {
        drinksFetchPromiseRef.current = null;
        if (isMountedRef.current) setLoading(false);
      });

    drinksFetchPromiseRef.current = p;
    return p;
  }, []);

  const fetchLookups = useCallback(async (opts?: { force?: boolean }) => {
    const force = Boolean(opts?.force);
    if (!force && lookupsFetchPromiseRef.current) return lookupsFetchPromiseRef.current;

    setLookupsLoading(true);
    const p = Promise.all([
      regionService.getAllRegions(),
      ingredientService.getAllIngredients(),
      flavorService.getAllFlavors(),
    ])
      .then(([regionsData, ingredientsData, flavorsData]) => {
        if (!isMountedRef.current) return;
        setRegions(regionsData);
        setIngredients(ingredientsData);
        setFlavors(flavorsData);
      })
      .catch((error) => {
        console.error('Error fetching lookup data:', error);
        if (isMountedRef.current) message.error('Không thể tải dữ liệu bộ lọc');
      })
      .finally(() => {
        lookupsFetchPromiseRef.current = null;
        if (isMountedRef.current) setLookupsLoading(false);
      });

    lookupsFetchPromiseRef.current = p;
    return p;
  }, []);

  const fetchDishes = useCallback(async (force = false): Promise<Dish[]> => {
    if (!force && dishesRef.current && dishesRef.current.length > 0) {
      return dishesRef.current;
    }

    try {
      const dishesData = await dishService.getAllDishes();
      dishesRef.current = dishesData;
      return dishesData;
    } catch (error) {
      console.error('Error fetching dishes:', error);
      return [];
    }
  }, [dishService]);

  const tourSteps: TourProps['steps'] = [
    {
      title: 'Quản lý đồ uống đóng chai',
      description: 'Chào mừng bạn đến với trang Quản lý đồ uống đóng chai! Đây là nơi bạn có thể quản lý danh sách các đồ uống đóng chai của cửa hàng, thêm mới, chỉnh sửa và cập nhật thông tin chi tiết.',
      target: () => titleRef.current,
    },
    {
      title: 'Tìm kiếm',
      description: 'Tìm kiếm nhanh các đồ uống đóng chai theo tên. Nhập từ khóa và kết quả sẽ được lọc ngay lập tức.',
      target: () => searchRef.current,
    },
    {
      title: 'Bộ lọc',
      description: 'Lọc danh sách đồ uống theo các tiêu chí như vùng trồng, hương vị và thành phần. Các bộ lọc có thể kết hợp với nhau để tìm chính xác đồ uống bạn cần.',
      target: () => filtersRef.current,
    },
    {
      title: 'Đồng bộ giá',
      description: 'Cập nhật giá đồ uống từ dữ liệu quản lý món ăn. Chức năng này giúp đồng bộ giá đồ uống với hệ thống IPOS.',
      target: () => syncButtonRef.current,
    },
    {
      title: 'Thêm đồ uống mới',
      description: 'Nhấn vào đây để thêm một đồ uống đóng chai mới vào hệ thống. Bạn sẽ cần điền các thông tin chi tiết như tên, mô tả, nguồn gốc, hương vị, v.v.',
      target: () => addButtonRef.current,
    },
    {
      title: 'Bảng dữ liệu đồ uống',
      description: 'Bảng hiển thị tất cả các đồ uống đóng chai đã được thêm vào hệ thống. Mỗi hàng chứa thông tin chi tiết về một đồ uống bao gồm hình ảnh, tên, mô tả, nguồn gốc và các nút thao tác.',
      target: () => tableContainerRef.current,
    },
    {
      title: 'Kết thúc hướng dẫn!',
      description: 'Cảm ơn bạn đã xem hướng dẫn này. Bây giờ bạn đã sẵn sàng quản lý danh mục đồ uống đóng chai của cửa hàng. Bạn có thể thêm mới, chỉnh sửa, xóa các đồ uống và sử dụng các bộ lọc để tìm kiếm nhanh chóng.',
      target: () => titleRef.current,
    },
  ];

  const modalTourSteps: TourProps['steps'] = [
    {
      title: 'Form thêm đồ uống đóng chai',
      description: 'Đây là form để thêm thông tin đồ uống đóng chai mới. Hãy điền đầy đủ các thông tin bắt buộc để tạo sản phẩm hoàn chỉnh.',
      target: () => modalTitleRef.current,
    },
    {
      title: 'Tên đồ uống',
      description: 'Nhập tên của đồ uống đóng chai (VD: Cold Brew Original, Iced Coffee Vanilla). Đây là thông tin bắt buộc và sẽ hiển thị cho khách hàng.',
      target: () => nameFieldRef.current,
    },
    {
      title: 'Mã đồ uống',
      description: 'Nhập mã định danh cho đồ uống (VD: CB001, ICE002). Mã này dùng để đồng bộ giá với hệ thống quản lý món ăn.',
      target: () => productCodeFieldRef.current,
    },
    {
      title: 'Mô tả đồ uống',
      description: 'Mô tả chi tiết về đồ uống như hương vị, đặc điểm, cách thức chế biến. Thông tin này giúp khách hàng hiểu rõ hơn về sản phẩm.',
      target: () => descriptionFieldRef.current,
    },
    {
      title: 'Loại cà phê',
      description: 'Nhập thông tin về loại cà phê được sử dụng để làm đồ uống (VD: Arabica Cầu Đất, Robusta Đắk Lắk).',
      target: () => coffeeFieldRef.current,
    },
    {
      title: 'Nguồn gốc',
      description: 'Chọn hoặc nhập nguồn gốc của cà phê sử dụng (VD: Cầu Đất, Đà Lạt, Guatemala). Có thể chọn nhiều nguồn gốc.',
      target: () => originFieldRef.current,
    },
    {
      title: 'Hương vị',
      description: 'Chọn hoặc nhập các note hương vị đặc trưng của đồ uống (VD: Chocolate, Caramel, Vanilla). Giúp khách hàng dễ dàng lựa chọn theo sở thích.',
      target: () => flavorFieldRef.current,
    },
    {
      title: 'Thành phần',
      description: 'Chọn hoặc nhập các thành phần có trong đồ uống (VD: Cà phê, Đường, Sữa, Vanilla). Thông tin quan trọng cho người có dị ứng.',
      target: () => ingredientsFieldRef.current,
    },
    {
      title: 'Thông tin sản phẩm',
      description: 'Nhập số ngày hết hạn và số lượng mua ban đầu. Số ngày hết hạn quan trọng để đảm bảo chất lượng sản phẩm.',
      target: () => expirationFieldRef.current,
    },
    {
      title: 'Dung tích và giá',
      description: 'Thêm các kích cỡ dung tích khác nhau và giá tương ứng (VD: 250ml - 35,000đ, 500ml - 65,000đ). Ít nhất phải có một kích cỡ.',
      target: () => volumesFieldRef.current,
    },
    {
      title: 'Hình ảnh sản phẩm',
      description: 'Thêm hình ảnh cho sản phẩm từ máy tính hoặc Google Drive. Ít nhất cần một hình ảnh. Có thể kéo thả để sắp xếp thứ tự hiển thị.',
      target: () => imagesFieldRef.current,
    },
    {
      title: 'Hoàn thành',
      description: (
        <div>
          <p>Kiểm tra lại tất cả thông tin và nhấn "Thêm mới" để lưu đồ uống vào hệ thống. Đảm bảo đã điền đầy đủ các thông tin bắt buộc.</p>
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <p style={{ fontWeight: 'bold', marginBottom: 8 }}>Preview: Đồ uống sau khi thêm thành công sẽ hiển thị như này:</p>
            <Image
              src={BottledDrinkManagementPreview}
              alt="Preview đồ uống sau khi thêm"
              style={{
                maxWidth: '100%',
                height: 'auto',
                maxHeight: '300px',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                cursor: 'pointer'
              }}
              preview={{
                mask: 'Click để phóng to'
              }}
            />
          </div>
        </div>
      ),
      target: () => submitButtonRef.current,
    },
  ];

  useEffect(() => {
    fetchDrinks();
    fetchLookups();
  }, [fetchDrinks, fetchLookups]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchText(searchText.trim());
    }, 300);

    return () => {
      window.clearTimeout(timer);
    };
  }, [searchText]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(prev => (prev !== window.innerWidth ? window.innerWidth : prev));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSubmit = async (values: any) => {
    try {
      setSubmitLoading(true);
      // Kiểm tra hình ảnh
      if (imageFiles.length === 0 && driveImages.length === 0 &&
        (!editingDrink || (!editingDrink.images?.length && !editingDrink.driveImages?.length))) {
        message.error('Vui lòng chọn ít nhất một hình ảnh');
        setSubmitLoading(false);
        return;
      }

      const oldLstImage = [...imageFilesPreview];
      const lstDriveImages = [...driveImages, ...driveImagesPreview].map((image: any) => ({
        fileId: image.fileId,
        webViewLink: image.webViewLink,
        fileName: image.fileName
      }));


      const formattedValues = {
        ...values,
        driveImages: lstDriveImages,
        coffeeId: '',
        // Đảm bảo purchaseCount luôn có giá trị, mặc định là 0
        purchaseCount: values.purchaseCount || 0,
        // Thêm createdAt và updatedAt
        volumes: values.volumes.map((v: any) => ({
          volume: Number(v.volume),
          price: Number(v.price)
        }))
      };

      // Thêm mới vùng trồng nếu chưa tồn tại
      await Promise.all([
        handleAddNewRegion(formattedValues.origin),
        handleAddNewIngredient(formattedValues.ingredients),
        handleAddNewFlavor(formattedValues.flavorNotes),
      ]);

      if (editingDrink) {
        console.log('updateBottledDrink');

        await bottledDrinkService.updateBottledDrink(
          editingDrink.id!,
          formattedValues,
          imageFiles.length > 0 ? imageFiles : undefined,
          oldLstImage.length > 0 ? oldLstImage : undefined
        );
      } else {
        await bottledDrinkService.addBottledDrink(formattedValues, imageFiles);
      }
      setModalVisible(false);
      form.resetFields();
      setImageFiles([]);
      setDriveImages([]);
      setImageFilesPreview([]);
      setDriveImagesPreview([]);
      await fetchDrinks({ force: true });
      setEditingDrink(null);
      message.success(`${editingDrink ? 'Cập nhật' : 'Thêm'} đồ uống thành công`);

      // Gọi api gửi dữ liệu đồ uống để AI tự động cập nhật
      // await axios.get(`https://coffee-ai.8am.vn/update`);
    } catch (error) {
      console.error('Error saving drink:', error);
      message.error('Có lỗi xảy ra khi lưu thông tin');
    } finally {
      // Always reset loading state regardless of success or failure
      setSubmitLoading(false);
    }
  };

  const handleDriveImageSelect = useCallback((fileId: string, webViewLink: string, fileName: string) => {
    setDriveImages(prev => [...prev, { fileId, webViewLink, fileName }]);
  }, []);

  const handlePreviewDriveImageSelect = useCallback((fileId: string, webViewLink: string, fileName: string) => {
    setDriveImagesPreview(prev => [...prev, { fileId, webViewLink, fileName }]);
  }, []);

  const handleEdit = useCallback((record: any) => {
    // Reset the loading state first
    setSubmitLoading(false);

    // Set up the drink for editing
    const driveImages = record.driveImages?.map((image: any) => ({
      fileId: image.fileId,
      webViewLink: image.webViewLink,
      fileName: image.fileName
    })) || [];

    setDriveImagesPreview(driveImages);

    const images = record.images || [];
    setImageFilesPreview(images);

    setEditingDrink(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  }, [form]);

  const renderImage = useCallback((images: string[], record: any) => {
    return (
      <Image.PreviewGroup>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          maxWidth: 300,
        }}>
          {(record.driveImages && record.driveImages.length > 0) && record.driveImages.map((image: any, index: any) => (
            <div key={index} style={{ marginBottom: 8 }}>
              <Image
                src={`https://lh3.googleusercontent.com/d/${image.fileId}?authuser=server`}
                alt={image.fileName}
                style={{ width: 50, height: 50, objectFit: 'cover' }}
              />
            </div>
          ))}

          {images && images.map((url, index) => (
            <div key={index} style={{ marginBottom: 8 }}>
              <Image
                src={url}
                alt={`drink-${index}`}
                width={50}
                height={50}
                style={{ objectFit: 'cover' }}
              />
            </div>
          ))}
        </div>
      </Image.PreviewGroup>
    );
  }, []);

  const handleDelete = useCallback((id: string) => {
    Modal.confirm({
      title: 'Xác nhận xóa',
      content: 'Bạn có chắc chắn muốn xóa đồ uống này không?',
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await bottledDrinkService.deleteBottledDrink(id);
          await fetchDrinks({ force: true });
          message.success('Xóa đồ uống thành công');

          // Gọi api gửi dữ liệu đồ uống để AI tự động cập nhật
          // await axios.get(`https://coffee-ai.8am.vn/update`);
        } catch (error) {
          console.error('Error deleting drink:', error);
          message.error('Không thể xóa đồ uống');
        }
      },
    });
  }, [fetchDrinks]);

  const columns: TableColumnsType<any> = useMemo(() => ([
    {
      title: 'Hình ảnh',
      dataIndex: 'images',
      key: 'images',
      width: 300,
      render: (images: string[], record: any) => renderImage(images, record)
    },
    {
      title: 'Mã đồ uống',
      dataIndex: 'productCode',
      key: 'productCode',
    },
    {
      title: 'Tên đồ uống',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Loại cà phê',
      dataIndex: 'coffeeOriginText',
      key: 'coffeeOriginText',
    },
    {
      title: 'Thành phần',
      dataIndex: 'ingredients',
      key: 'ingredients',
      render: (ingredients: string[]) => ingredients?.map(ingredient => <Tag key={ingredient}>{ingredient}</Tag>)
    },
    {
      title: 'Nguồn gốc',
      dataIndex: 'origin',
      key: 'origin',
      render: (origin: string[]) => origin.map(region => <Tag key={region}>{region}</Tag>)
    },
    {
      title: 'Hương vị',
      dataIndex: 'flavorNotes',
      key: 'flavorNotes',
      render: (notes: string[]) => notes.map(flavor => <Tag key={flavor}>{flavor}</Tag>)
    },
    {
      title: 'Số ngày hết hạn',
      dataIndex: 'expirationDays',
      key: 'expirationDays',
      render: (days: number) => `${days} ngày`
    },
    {
      title: 'Số lượng mua',
      dataIndex: 'purchaseCount',
      key: 'purchaseCount',
      width: 150,
      render: (count: number) => count || 0
    },
    {
      title: 'Dung tích và giá',
      dataIndex: 'volumes',
      key: 'volumes',
      render: (volumes: { volume: number; price: number }[]) => (
        <div>
          {volumes?.map((item, index) => (
            <div key={index}>
              {item.volume}ml - {item.price.toLocaleString('vi-VN')}đ
            </div>
          ))}
        </div>
      )
    },
    {
      title: 'Thao tác',
      key: 'action',
      fixed: 'right',
      width: 100,
      render: (_: any, record: BottledDrink) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => {
              handleEdit(record);
            }}
          />
          <Button
            icon={<DeleteOutlined />}
            danger
            onClick={() => handleDelete(record.id!)}
          />
        </Space>
      )
    }
  ]), [handleDelete, handleEdit, renderImage]);

  const mobileColumns: TableColumnsType<any> = useMemo(() => ([
    {
      title: 'Thông tin',
      dataIndex: 'name',
      key: 'info',
      render: (_: any, record: BottledDrink) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            <div style={{ width: 40, height: 40 }}>
              <Image.PreviewGroup>
                <div style={{ display: 'flex', gap: 8 }}>
                  {(record.driveImages && record.driveImages.length > 0) && record.driveImages.slice(0, 1).map((image: any, index: any) => (
                    <Image
                      key={index}
                      src={`https://lh3.googleusercontent.com/d/${image.fileId}?authuser=server`}
                      alt={image.fileName}
                      style={{ width: 40, height: 40, objectFit: 'cover' }}
                    />
                  ))}
                  {record.images && record.images.slice(0, 1).map((url, index) => (
                    <Image
                      key={index}
                      src={url}
                      alt={`drink-${index}`}
                      style={{ width: 40, height: 40, objectFit: 'cover' }}
                    />
                  ))}
                </div>
              </Image.PreviewGroup>
            </div>
            <div style={{ flex: 1 }}>
              <strong>{record.name}</strong>
              <div style={{
                marginTop: 4,
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                fontSize: '12px',
                color: '#666'
              }}>
                {record.description}
              </div>
            </div>
          </div>

          <div style={{ fontSize: '12px' }}>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <div>
                <strong>Loại cà phê:</strong>{' '}
                <Tag style={{ fontSize: '11px' }}>{record.coffeeOriginText}</Tag>
              </div>

              <div>
                <strong>Nguồn gốc:</strong>{' '}
                {Array.isArray(record.origin) && record.origin.map(r => (
                  <Tag key={r} style={{ fontSize: '11px' }}>{r}</Tag>
                ))}
              </div>

              <div>
                <strong>Hương vị:</strong>{' '}
                {record.flavorNotes?.map(flavor => (
                  <Tag key={flavor} style={{ fontSize: '11px' }}>{flavor}</Tag>
                ))}
              </div>

              <div>
                <strong>Thành phần:</strong>{' '}
                {record.ingredients?.map((ingredient: any) => (
                  <Tag key={ingredient.id} style={{ fontSize: '11px' }}>{ingredient}</Tag>
                ))}
              </div>

              <div>
                <strong>Dung tích và giá:</strong>{' '}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {record.volumes?.map((item, index) => (
                    <Tag key={index} color="orange" style={{ fontSize: '11px' }}>
                      {item.volume}ml - {item.price.toLocaleString()}đ
                    </Tag>
                  ))}
                </div>
              </div>

              <div>
                <strong>Hết hạn:</strong>{' '}
                <Tag style={{ fontSize: '11px' }}>{record.expirationDays} ngày</Tag>
              </div>

              <div>
                <strong>Số lượng mua:</strong>{' '}
                <Tag color="purple" style={{ fontSize: '11px' }}>{record.purchaseCount || 0}</Tag>
              </div>

              {record.productCode && (
                <div>
                  <strong>Mã đồ uống:</strong>{' '}
                  <Tag color="cyan" style={{ fontSize: '11px' }}>{record.productCode}</Tag>
                </div>
              )}
            </Space>
          </div>
        </div>
      )
    },
    {
      title: 'Thao tác',
      key: 'action',
      render: (_: any, record: BottledDrink) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Button
            icon={<DeleteOutlined />}
            danger
            onClick={() => handleDelete(record.id!)}
          />
        </Space>
      )
    }
  ]), [handleDelete, handleEdit]);

  const handleRemoveLocalImage = useCallback((index: number) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleRemoveLocalPreviewImage = useCallback((index: number) => {
    setImageFilesPreview((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleRemoveDriveImage = useCallback((index: number) => {
    setDriveImages((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleRemoveDrivePreviewImage = useCallback((index: number) => {
    setDriveImagesPreview((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleImagesChange = useCallback((files: File[]) => {
    setImageFiles(files);
  }, []);

  const handleAddNewRegion = async (values: string[]) => {
    if (!values || values.length === 0) return;

    const existingRegionNames = new Set(regions.map(r => r.name));
    const missingRegions = Array.from(new Set(values)).filter(value => !existingRegionNames.has(value));

    if (missingRegions.length === 0) return;

    try {
      await Promise.all(
        missingRegions.map(value =>
          regionService.addRegion({
            name: value,
            description: `Vùng trồng ${value}`,
            country: '',
            altitude: {
              min: 0,
              max: 0
            },
            climate: '',
            soil: '',
            createdAt: new Date(),
            updatedAt: new Date()
          })
        )
      );
      const refreshedRegions = await regionService.getAllRegions();
      setRegions(refreshedRegions);
    } catch (error) {
      console.error('Error adding new region:', error);
      message.error('Không thể thêm vùng trồng mới');
    }
  };

  const handleAddNewIngredient = async (values: string[]) => {
    if (!values || values.length === 0) return;

    const existingIngredientNames = new Set(ingredients.map(i => i.name));
    const missingIngredients = Array.from(new Set(values)).filter(value => !existingIngredientNames.has(value));

    if (missingIngredients.length === 0) return;

    try {
      await Promise.all(missingIngredients.map(value => ingredientService.addIngredient(value)));
      const refreshedIngredients = await ingredientService.getAllIngredients();
      setIngredients(refreshedIngredients);
    } catch (error) {
      console.error('Error adding new ingredient:', error);
      message.error('Không thể thêm thành phần mới');
    }
  };

  const handleAddNewFlavor = async (values: string[]) => {
    if (!values || values.length === 0) return;

    const existingFlavorNames = new Set(flavors.map(f => f.name));
    const missingFlavors = Array.from(new Set(values)).filter(value => !existingFlavorNames.has(value));

    if (missingFlavors.length === 0) return;

    try {
      await Promise.all(
        missingFlavors.map(value =>
          flavorService.addFlavor({
            name: value,
            description: `Hương vị ${value}`,
            intensity: 5,
            createdAt: new Date(),
            updatedAt: new Date()
          })
        )
      );
      const refreshedFlavors = await flavorService.getAllFlavors();
      setFlavors(refreshedFlavors);
    } catch (error) {
      console.error('Error adding new flavor:', error);
      message.error('Không thể thêm hương vị mới');
    }
  };

  const filteredDrinks = useMemo(() => {
    return drinks.filter(drink => {
      const matchesSearch = drink.name.toLowerCase().includes(debouncedSearchText.toLowerCase());
      const matchesRegion = regionFilter ? drink.origin.includes(regionFilter) : true;
      const matchesFlavor = flavorFilter ? drink.flavorNotes.includes(flavorFilter) : true;
      const matchesIngredient = ingredientFilter ? drink.ingredients.map(i => i.name).includes(ingredientFilter) : true;
      return matchesSearch && matchesRegion && matchesFlavor && matchesIngredient;
    });
  }, [drinks, debouncedSearchText, regionFilter, flavorFilter, ingredientFilter]);

  const handleSyncPrices = async () => {
    setSyncLoading(true);
    try {
      const dishesData = await fetchDishes();

      if (dishesData.length === 0) {
        message.info('Không có dữ liệu món ăn để đồng bộ giá');
        return;
      }

      const result = await bottledDrinkService.syncPricesWithDishes(dishesData);

      if (result.success && result.updatedCount > 0) {
        // Show more detailed success message with sync details
        if (result.syncDetails && result.syncDetails.length > 0) {
          Modal.success({
            title: `Đã đồng bộ giá cho ${result.updatedCount} đồ uống`,
            content: (
              <div>
                <p>Chi tiết đồng bộ giá:</p>
                <ul style={{ maxHeight: '300px', overflow: 'auto' }}>
                  {result.syncDetails.map((detail, index) => (
                    <li key={index}>
                      {detail.drinkName} (Mã: {detail.productCode}):
                      {detail.oldPrice.toLocaleString('vi-VN')}đ →
                      <strong>{detail.newPrice.toLocaleString('vi-VN')}đ</strong>
                    </li>
                  ))}
                </ul>
              </div>
            )
          });
        } else {
          message.success(result.message || `Đã đồng bộ giá cho ${result.updatedCount} đồ uống`);
        }

        // Reload the drinks list to reflect the updates
        await fetchDrinks({ force: true });
      } else {
        // Show appropriate message for various error cases
        message.info(result.message || 'Không có đồ uống nào được đồng bộ. Kiểm tra lại mã đồ uống của bạn.');
      }
    } catch (error) {
      console.error('Error syncing prices:', error);
      message.error('Không thể đồng bộ giá với món ăn: ' + error);
    } finally {
      setSyncLoading(false);
    }
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setIsModalTourOpen(false);
    setImageFiles([]);
    setDriveImages([]);
    setImageFilesPreview([]);
    setDriveImagesPreview([]);
    setSubmitLoading(false);
    form.resetFields();
  };

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div className="admin-page-title">
          <h1 ref={titleRef}>Quản lý đồ uống đóng chai</h1>
          <Tag color="blue">{drinks.length} đồ uống</Tag>
          <Button
            type="primary"
            icon={<QuestionCircleOutlined />}
            onClick={() => setIsTourOpen(true)}
            style={{ marginLeft: 8 }}
          >
            Hướng dẫn
          </Button>
        </div>

        <div className="admin-page-actions">
          <div className="admin-search-filters" ref={filtersRef}>
            <div ref={searchRef}>
              <Search
                placeholder="Tìm theo tên đồ uống"
                allowClear
                value={searchText}
                style={{ width: isMobile ? '100%' : 300 }}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
            <Select
              placeholder="Lọc theo vùng"
              style={{ width: isMobile ? '100%' : 200 }}
              loading={lookupsLoading}
              allowClear
              onChange={(value) => setRegionFilter(value)}
              showSearch
              filterOption={(input, option) =>
                (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {regions.map(region => (
                <Select.Option key={region.id} value={region.name}>
                  {region.name}
                </Select.Option>
              ))}
            </Select>
            <Select
              placeholder="Lọc theo hương vị"
              style={{ width: isMobile ? '100%' : 200 }}
              loading={lookupsLoading}
              allowClear
              onChange={(value) => setFlavorFilter(value)}
              showSearch
              filterOption={(input, option) =>
                (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {flavors.map(flavor => (
                <Select.Option key={flavor.id} value={flavor.name}>
                  {flavor.name}
                </Select.Option>
              ))}
            </Select>
            <Select
              placeholder="Lọc theo thành phần"
              style={{ width: isMobile ? '100%' : 200 }}
              loading={lookupsLoading}
              allowClear
              onChange={(value) => setIngredientFilter(value)}
              showSearch
              filterOption={(input, option) =>
                (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {ingredients.map(ingredient => (
                <Select.Option key={ingredient.id} value={ingredient.name}>
                  {ingredient.name}
                </Select.Option>
              ))}
            </Select>
          </div>

          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingDrink(null);
                setSubmitLoading(false);
                form.resetFields();
                setImageFiles([]);
                setModalVisible(true);
                setDriveImages([]);
                setDriveImagesPreview([]);
                setImageFilesPreview([]);
              }}
              className="admin-button"
              ref={addButtonRef}
            >
              Thêm đồ uống mới
            </Button>
            <Button
              type="primary"
              icon={<SyncOutlined />}
              onClick={handleSyncPrices}
              loading={syncLoading}
              className="admin-button"
              ref={syncButtonRef}
            >
              Đồng bộ giá
            </Button>
          </Space>
        </div>
      </div>

      <div className="admin-table-container" ref={tableContainerRef}>
        <Table
          className="admin-table"
            columns={isMobile ? mobileColumns : columns}
          dataSource={filteredDrinks}
          loading={loading}
          rowKey="id"
          scroll={{ x: isMobile ? undefined : 2500 }}
          pagination={{
            responsive: true,
            defaultPageSize: isMobile ? 5 : 10,
            position: isMobile ? ['bottomCenter'] : ['bottomRight']
          }}
        />
      </div>

      {/* Tour component */}
      <ConfigProvider locale={viVN}>
        <Tour
          open={isTourOpen}
          onClose={() => setIsTourOpen(false)}
          steps={tourSteps}
          indicatorsRender={(current, total) => (
            <span>{current + 1} / {total}</span>
          )}
          type="primary"
          arrow={true}
          placement="bottom"
          mask={{
            style: {
              boxShadow: 'inset 0 0 15px #333',
            },
          }}
        />
      </ConfigProvider>

      <Modal
        title={editingDrink ? "Sửa thông tin đồ uống" : "Thêm đồ uống mới"}
        visible={modalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={800}
        className="admin-modal"
        destroyOnClose={true}
      >
        <div className="admin-form-container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 ref={modalTitleRef} style={{ margin: 0 }}>
              {editingDrink ? "Cập nhật thông tin đồ uống đóng chai" : "Thêm đồ uống đóng chai mới"}
            </h3>
            <Button
              type="primary"
              icon={<QuestionCircleOutlined />}
              onClick={() => {
                setIsModalTourOpen(true);
              }}
              size="small"
            >
              Hướng dẫn điền form
            </Button>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <BottledDrinkForm
              onImagesChange={handleImagesChange}
              onDriveImageSelect={handleDriveImageSelect}
              onDriveImagePreviewSelect={handlePreviewDriveImageSelect}
              onImagesPreviewChange={setImageFilesPreview}
              selectedFiles={imageFiles}
              selectedFilesPreview={imageFilesPreview}
              driveImages={driveImages}
              driveImagesPreview={driveImagesPreview}
              editingDrink={editingDrink}
              onRemoveLocalImage={handleRemoveLocalImage}
              onRemoveLocalPreviewImage={handleRemoveLocalPreviewImage}
              onRemoveDriveImage={handleRemoveDriveImage}
              onRemoveDrivePreviewImage={handleRemoveDrivePreviewImage}
              setDriveImages={setDriveImages}
              setDriveImagesPreview={setDriveImagesPreview}
              regions={regions}
              flavors={flavors}
              ingredients={ingredients}
              regionsLoading={lookupsLoading}
              flavorsLoading={lookupsLoading}
              ingredientsLoading={lookupsLoading}
              // Pass refs
              nameFieldRef={nameFieldRef}
              descriptionFieldRef={descriptionFieldRef}
              productCodeFieldRef={productCodeFieldRef}
              coffeeFieldRef={coffeeFieldRef}
              originFieldRef={originFieldRef}
              flavorFieldRef={flavorFieldRef}
              ingredientsFieldRef={ingredientsFieldRef}
              volumesFieldRef={volumesFieldRef}
              expirationFieldRef={expirationFieldRef}
              imagesFieldRef={imagesFieldRef}
            />
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitLoading}
                disabled={submitLoading}
                className="admin-button"
                ref={submitButtonRef}
              >
                {editingDrink ? 'Cập nhật' : 'Thêm mới'}
              </Button>
            </Form.Item>
          </Form>
        </div>

        {/* Modal Tour component */}
        <ConfigProvider locale={viVN}>
          <Tour
            open={isModalTourOpen}
            onClose={() => setIsModalTourOpen(false)}
            steps={modalTourSteps}
            indicatorsRender={(current, total) => (
              <span>{current + 1} / {total}</span>
            )}
            type="primary"
            arrow={true}
            placement="bottom"
            mask={{
              style: {
                boxShadow: 'inset 0 0 15px #333',
              },
            }}
          />
        </ConfigProvider>
      </Modal>
    </AdminLayout>
  );
};

export default BottledDrinkManagement;
